/**
 * Case-insensitive email comparison.
 */
export const emailsMatch = (a?: string, b?: string): boolean =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase()

/**
 * Opens the default email client with pre-filled details using the URL API,
 * ensuring spaces in subject/body are encoded as %20 for mailto compatibility.
 *
 * @param to - The primary recipient's email address.
 * @param cc - Optional array of CC recipients' email addresses.
 * @param subject - Optional subject line for the email.
 * @param body - Optional body text for the email.
 */
export const openEmailClient = ({
  to,
  cc,
  subject,
  body,
}: {
  to: string
  cc?: string[]
  subject?: string
  body?: string
}) => {
  // Use the URL constructor
  const url = new URL(`mailto:${encodeURIComponent(to)}`) // Manually encode 'to' just in case

  // Append parameters using URLSearchParams
  if (cc && cc.length > 0) {
    url.searchParams.append('cc', cc.join(','))
  }
  if (subject) {
    url.searchParams.append('subject', subject)
  }
  if (body) {
    // Trim trailing whitespace first
    url.searchParams.append('body', body.trimEnd())
  }

  // Generate the standard URL string (might have + for spaces)
  let mailtoLink = url.toString()

  // Manually replace + with %20 in the query string part for mailto compatibility
  const queryStartIndex = mailtoLink.indexOf('?')
  if (queryStartIndex !== -1) {
    const base = mailtoLink.substring(0, queryStartIndex)
    const queryString = mailtoLink.substring(queryStartIndex + 1)
    // Replace all occurrences of + with %20
    const correctedQueryString = queryString.replace(/\+/g, '%20')
    mailtoLink = `${base}?${correctedQueryString}`
  }

  // Use window.location.href to open the default email client
  if (typeof window !== 'undefined') {
    window.location.href = mailtoLink
  }
}
